import React from "react";
import { useAuth, useUser } from "@clerk/nextjs";
import { useRouter } from "next/router";
import { datadogRum } from "@datadog/browser-rum";
import { Button, Text } from "design_system";
import useClerkMetadata from "features/User/hooks/useClerkMetadata";
import { useOnboardingData } from "features/User/hooks/useOnboardingData";
import useSubscription from "features/User/hooks/useSubscription";
import { getUserStudentEmail } from "helpers/email";
import { getSubscriptionTier } from "helpers/subscription";
import useLabels from "hooks/useLabels";
import { useAppDispatch } from "hooks/useStore";
import { replaceTopModal } from "store/slices/modalStack";
import {
  STUDENT_DISCOUNT_MODAL_ID,
  type StudentDiscountModalProps,
} from "../StudentDiscountModal";
import { UserResource } from "@clerk/types";
import { Ticket } from "lucide-react";
import {
  CLAIM_STUDENT_DISCOUNT_MODAL_ID,
  ClaimStudentDiscountModalProps,
} from "../ClaimStudentDiscountModal";
import {
  MixpanelActionSource,
  MixpanelEvent,
  ClickStudentDiscountBannerEvent,
  MixpanelEventVersion,
} from "helpers/services/mixpanel/events";
import { trackEventServerSide } from "helpers/api";
import path from "constants/path";

const STUDENT_DISCOUNT_SOURCE = "student_discount";

/**
 * Banner component that promotes student discount offer.
 * Shows for:
 * - Non-logged-in users
 * - Users who haven't completed onboarding
 * - Users with student roles (including those without student email)
 * - Free users who haven't used their student trial
 *
 * Handles flows:
 * 1. Non-logged-in users -> Redirect to sign-up page
 * 2. No onboarding completed -> Opens ClaimStudentDiscountModal
 * 3. Student role without email -> Opens ClaimStudentDiscountModal to add email
 * 4. Student role with email -> Opens StudentDiscountModal (type based on subscription)
 *    - Premium users get "deep" type for Deep plan upgrade
 *    - Free users get "premium" type for Pro plan with trial
 */
function StudentDiscountBanner({
  className,
  actionSource,
}: {
  className?: string;
  actionSource: MixpanelActionSource;
}) {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const router = useRouter();
  const subscription = useSubscription();
  const { userMetadata } = useClerkMetadata();
  const { hasCompletedOnboarding, isStudentRole, role } = useOnboardingData();
  const studentEmail = getUserStudentEmail(user as unknown as UserResource);
  const dispatch = useAppDispatch();
  const [labels] = useLabels("student-discount.student-discount-banner");

  // Determine subscription tier
  const subscriptionTier = getSubscriptionTier(subscription?.subscription);
  const isPremiumUser = subscriptionTier === "premium";

  // Wait for auth to be loaded
  if (!isLoaded) {
    return null;
  }

  // For logged-in users, also check metadata
  if (isSignedIn && !userMetadata) {
    return null;
  }

  // Determine if banner should be hidden for logged-in users
  const completedOnboardingAsNonStudent =
    isSignedIn && hasCompletedOnboarding && !isStudentRole();
  const freeUserAlreadyUsedTrial =
    isSignedIn && userMetadata?.hasUsedStudentTrial && !isPremiumUser;
  const hasPaidSubscription = !!(
    subscription?.subscription?.user || subscription?.subscription?.org
  );
  const isNonPremiumPaidUser =
    isSignedIn && hasPaidSubscription && !isPremiumUser;

  // Hide banner if any of these conditions are true (only apply to logged-in users)
  if (
    completedOnboardingAsNonStudent || // Covers ALL users (including premium) who completed onboarding as non-student
    freeUserAlreadyUsedTrial ||
    isNonPremiumPaidUser
  ) {
    return null;
  }

  const handleClaimClick = () => {
    // Track the click event for all cases
    const eventData: ClickStudentDiscountBannerEvent = {
      event: MixpanelEvent.ClickStudentDiscountBanner,
      version: MixpanelEventVersion.Threads,
      actionSource,
      isSignedIn: !!isSignedIn,
    };
    trackEventServerSide(eventData);

    // Case 1: Non-logged-in user -> Redirect to sign-up
    if (!isSignedIn) {
      router.push(
        `${path.SIGN_UP}#/?redirect_url=${encodeURIComponent(router.asPath)}`
      );
    }
    // Case 2: User hasn't completed onboarding -> Claim flow
    else if (!hasCompletedOnboarding) {
      const modalProps: ClaimStudentDiscountModalProps = {
        actionSource,
      };
      dispatch(
        replaceTopModal({
          id: CLAIM_STUDENT_DISCOUNT_MODAL_ID,
          blocksScroll: true,
          props: modalProps,
        })
      );
    }
    // Case 3: Student role WITHOUT student email -> Claim flow to add email
    else if (isStudentRole() && !studentEmail) {
      const modalProps: ClaimStudentDiscountModalProps = {
        actionSource,
      };
      dispatch(
        replaceTopModal({
          id: CLAIM_STUDENT_DISCOUNT_MODAL_ID,
          blocksScroll: true,
          props: modalProps,
        })
      );
    }
    // Case 4: Student role WITH student email -> Discount modal (type based on subscription)
    else if (isStudentRole() && studentEmail) {
      const modalType = isPremiumUser ? "deep" : "premium";
      const modalProps: StudentDiscountModalProps = {
        type: modalType,
        role,
        actionSource,
      };
      dispatch(
        replaceTopModal({
          id: STUDENT_DISCOUNT_MODAL_ID,
          blocksScroll: true,
          props: modalProps,
        })
      );
    }
    // Case 5: Shouldn't happen - completed onboarding as non-student
    else {
      const errorMessage =
        "StudentDiscountBanner: Unexpected state - banner shown but no valid action";
      const errorContext = {
        hasCompletedOnboarding,
        isStudentRole: isStudentRole(),
        role,
        studentEmail,
      };

      // Log to console for development
      console.error(errorMessage, errorContext);

      // Log to DataDog if available
      if (typeof window !== "undefined" && datadogRum) {
        datadogRum.addError(new Error(errorMessage), {
          source: STUDENT_DISCOUNT_SOURCE,
          context: {
            errorType: "unexpected_banner_state",
            component: "StudentDiscountBanner",
            ...errorContext,
            timestamp: new Date().toISOString(),
            url: window.location.href,
          },
        });
      }
    }
  };

  return (
    <div className={className}>
      <div
        className={
          "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-bg-faint rounded-lg"
        }
      >
        <div className="flex-1 flex flex-col gap-1">
          <div className="flex gap-3 items-center">
            <Ticket
              size={20}
              className="text-chart-green-base"
              strokeWidth={2}
            />
            <Text as="h3" size="sm" weight="bold">
              {isSignedIn && isPremiumUser
                ? labels["title-pro"]
                : labels["title"]}
            </Text>
          </div>
          <Text size="x-sm" theme="muted">
            {isSignedIn && isPremiumUser
              ? labels["description-pro"]
              : labels["description"]}
          </Text>
        </div>
        <Button
          variant="tertiary"
          size="sm"
          onClick={handleClaimClick}
          className="w-full sm:w-auto"
        >
          {labels["claim-button"]}
        </Button>
      </div>
    </div>
  );
}

export default StudentDiscountBanner;
